/**
 * SearchSelect — combobox accesibil cu căutare (port pentru apps/app, 1:1 cu tokenii).
 *
 * Buton-trigger cu valoarea curentă; la deschidere → dropdown cu câmp de căutare
 * (autofocus), filtrare instantanee (case + diacritice-insensitive), navigare cu
 * săgeți ↑/↓, Enter selectează, Escape/Tab închide, click în afară închide, scroll
 * automat la opțiunea evidențiată, highlight pe potrivire, stare „niciun rezultat".
 * Suportă: disabled, `creatable`, `clearable`, grupuri, descrieri. Controlat
 * (`value`/`onChange`). Dimensiuni aliniate cu clasa `.input` din global.css.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

export interface SearchOption {
  value: string;
  label: string;
  description?: string;
  group?: string;
  disabled?: boolean;
}

export interface SearchSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  creatable?: boolean;
  clearable?: boolean;
  emptyLabel?: string;
  id?: string;
  'aria-label'?: string;
  'data-testid'?: string;
  style?: CSSProperties;
  width?: number | string;
}

function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

function Highlight({ text, query }: { text: string; query: string }): ReactNode {
  const q = fold(query);
  if (!q) return text;
  const folded = fold(text);
  const idx = folded.indexOf(q);
  if (idx < 0) return text;
  const start = idx;
  const end = idx + q.length;
  return (
    <>
      {text.slice(0, start)}
      <mark style={{ background: 'var(--accent-soft)', color: 'var(--accent-text)', borderRadius: 3, padding: 0 }}>
        {text.slice(start, end)}
      </mark>
      {text.slice(end)}
    </>
  );
}

const inputBase: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: 'var(--bg)',
  border: '1.5px solid var(--line)',
  borderRadius: 11,
  padding: '13px 14px',
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--ink)',
};

export function SearchSelect({
  value,
  onChange,
  options,
  placeholder = 'Alege…',
  searchPlaceholder = 'Caută…',
  disabled = false,
  creatable = false,
  clearable = false,
  emptyLabel = 'Niciun rezultat',
  id,
  'aria-label': ariaLabel,
  'data-testid': testId,
  style,
  width,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);

  const reactId = useId();
  const listboxId = `${id ?? reactId}-listbox`;

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);
  const triggerLabel = selected?.label ?? (value ? value : '');

  const filtered = useMemo(() => {
    const q = fold(query);
    if (!q) return options;
    return options.filter((o) => fold(o.label).includes(q) || fold(o.value).includes(q) || (o.description ? fold(o.description).includes(q) : false));
  }, [options, query]);

  const trimmed = query.trim();
  const showCreate =
    creatable && trimmed.length > 0 && !options.some((o) => o.value === trimmed || fold(o.label) === fold(trimmed));

  const navItems = useMemo(() => {
    const items: Array<{ kind: 'option'; option: SearchOption } | { kind: 'create'; value: string }> = filtered
      .filter((o) => !o.disabled)
      .map((o) => ({ kind: 'option' as const, option: o }));
    if (showCreate) items.push({ kind: 'create', value: trimmed });
    return items;
  }, [filtered, showCreate, trimmed]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    setQuery('');
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    optionRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  useEffect(() => {
    if (!open) return undefined;
    function onDocPointer(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocPointer);
    return () => document.removeEventListener('mousedown', onDocPointer);
  }, [open]);

  const commit = useCallback(
    (v: string) => {
      onChange(v);
      setOpen(false);
      setQuery('');
    },
    [onChange],
  );

  const selectNav = useCallback(
    (i: number) => {
      const item = navItems[i];
      if (!item) return;
      if (item.kind === 'create') commit(item.value);
      else commit(item.option.value);
    },
    [navItems, commit],
  );

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlight((h) => (navItems.length === 0 ? 0 : (h + 1) % navItems.length));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlight((h) => (navItems.length === 0 ? 0 : (h - 1 + navItems.length) % navItems.length));
        break;
      case 'Home':
        e.preventDefault();
        setHighlight(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlight(Math.max(0, navItems.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        selectNav(highlight);
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  }

  const rootStyle: CSSProperties = { position: 'relative', width: width ?? '100%', ...style };
  optionRefs.current = [];
  let navIdx = -1;
  const activeId = open && navItems[highlight] ? `${listboxId}-opt-${highlight}` : undefined;
  const hasGroups = filtered.some((o) => o.group);

  function renderOption(o: SearchOption) {
    navIdx += 1;
    const myNav = navIdx;
    const isHighlighted = myNav === highlight && !o.disabled;
    const optId = `${listboxId}-opt-${myNav}`;
    return (
      <li
        key={o.value}
        id={o.disabled ? undefined : optId}
        ref={(el) => {
          if (!o.disabled) optionRefs.current[myNav] = el;
        }}
        role="option"
        aria-selected={o.value === value}
        aria-disabled={o.disabled || undefined}
        onMouseEnter={() => !o.disabled && setHighlight(myNav)}
        onMouseDown={(e) => {
          e.preventDefault();
          if (!o.disabled) commit(o.value);
        }}
        data-testid={testId ? `${testId}-option-${o.value}` : undefined}
        style={{
          listStyle: 'none',
          padding: '10px 12px',
          borderRadius: 9,
          cursor: o.disabled ? 'not-allowed' : 'pointer',
          opacity: o.disabled ? 0.4 : 1,
          background: isHighlighted ? 'var(--accent-soft)' : o.value === value ? 'var(--bg2)' : 'transparent',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14.5, fontWeight: o.value === value ? 800 : 600 }}>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Highlight text={o.label} query={query} />
          </span>
          {o.value === value && <span style={{ marginLeft: 'auto', color: 'var(--accent-text)', flex: 'none' }}>✓</span>}
        </span>
        {o.description && (
          <span style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Highlight text={o.description} query={query} />
          </span>
        )}
      </li>
    );
  }

  const grouped = useMemo(() => {
    const map = new Map<string, SearchOption[]>();
    for (const o of filtered) {
      const g = o.group ?? '';
      const arr = map.get(g);
      if (arr) arr.push(o);
      else map.set(g, [o]);
    }
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <div ref={rootRef} style={rootStyle}>
      <button
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        disabled={disabled}
        data-testid={testId}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        style={{ ...inputBase, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: triggerLabel ? 'var(--ink)' : 'var(--muted)',
          }}
        >
          {triggerLabel || placeholder}
        </span>
        {clearable && value && !disabled && (
          <span
            role="button"
            aria-label="Golește"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
            }}
            style={{ flex: 'none', color: 'var(--muted)', fontSize: 17, lineHeight: 1, padding: '0 2px' }}
          >
            ×
          </span>
        )}
        <span aria-hidden style={{ flex: 'none', color: 'var(--muted)', fontSize: 11, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
          ▾
        </span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 60,
            width: '100%',
            minWidth: 200,
            maxWidth: 'calc(100vw - 24px)',
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 13,
            boxShadow: 'var(--shadow)',
            padding: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={activeId}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={searchPlaceholder}
            data-testid={testId ? `${testId}-search` : undefined}
            style={{ ...inputBase, padding: '10px 12px', fontSize: 14.5 }}
          />
          <ul
            id={listboxId}
            role="listbox"
            aria-label={ariaLabel}
            style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}
          >
            {filtered.length === 0 && !showCreate && (
              <li role="option" aria-disabled style={{ listStyle: 'none', padding: '12px', fontSize: 13.5, color: 'var(--muted)', textAlign: 'center' }}>
                {emptyLabel}
              </li>
            )}
            {hasGroups
              ? grouped.map(([g, opts]) => (
                  <li key={g || '_'} role="presentation" style={{ listStyle: 'none' }}>
                    {g && (
                      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)', padding: '8px 12px 3px' }}>
                        {g}
                      </div>
                    )}
                    <ul role="group" aria-label={g || undefined} style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {opts.map((o) => renderOption(o))}
                    </ul>
                  </li>
                ))
              : filtered.map((o) => renderOption(o))}
            {showCreate &&
              (() => {
                navIdx += 1;
                const myNav = navIdx;
                const isHighlighted = myNav === highlight;
                return (
                  <li
                    key="__create__"
                    id={`${listboxId}-opt-${myNav}`}
                    ref={(el) => {
                      optionRefs.current[myNav] = el;
                    }}
                    role="option"
                    aria-selected={false}
                    onMouseEnter={() => setHighlight(myNav)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      commit(trimmed);
                    }}
                    data-testid={testId ? `${testId}-create` : undefined}
                    style={{
                      listStyle: 'none',
                      padding: '10px 12px',
                      borderRadius: 9,
                      cursor: 'pointer',
                      background: isHighlighted ? 'var(--accent-soft)' : 'transparent',
                      fontSize: 14.5,
                      fontWeight: 700,
                      color: 'var(--accent-text)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span aria-hidden>+</span>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Folosește „{trimmed}"</span>
                  </li>
                );
              })()}
          </ul>
        </div>
      )}
    </div>
  );
}
